/**
 * Scan / X-Ray status helpers.
 *
 * A scan carries a top level `status` plus a set of "sub statuses" — one per
 * step of the pipeline (cloning, product discovery, context injection,
 * analysis, publishing). The API uses a handful of raw values per step; for
 * display purposes they all collapse into five tones plus an "unknown" tone for
 * missing values.
 *
 * Note `reports_url_generation_status` is deliberately absent: the scan detail
 * page still surfaces that step, but it is not a column on the list.
 */

export type ScanStatusTone =
  | 'completed'
  | 'in-progress'
  | 'in-review'
  | 'failed'
  | 'timed-out'
  | 'aborted'
  | 'pending'
  | 'unknown';

/**
 * `TIMED_OUT` gets its own tone rather than folding into `failed`: a run that
 * ran out of time is retryable in a way a genuine failure is not, and the two
 * need different colours for that difference to be visible on the list.
 *
 * `ABORTED` is likewise not a failure — nothing went wrong, an operator simply
 * called the run off — so it gets a neutral tone of its own rather than sharing
 * either red.
 *
 * `IN_REVIEW` is split out of `in-progress` for the same class of reason: the
 * pipeline is not working, it is blocked on a human opening the scan and
 * approving the product config. Only `product_discovery_status` ever carries the
 * value, and `processing.hbs` has always drawn it as an amber "In Review" pill —
 * so the dot matching that is the list catching up with the detail page rather
 * than a new convention. `REVIEWED` stays `in-progress`: that one means the
 * review has been submitted and the pipeline is moving again.
 */
const TONE_BY_STATUS: Record<string, ScanStatusTone> = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'in-progress',
  INITIATE: 'in-progress',
  IN_REVIEW: 'in-review',
  REVIEWED: 'in-progress',
  FAILED: 'failed',
  TIMED_OUT: 'timed-out',
  ABORTED: 'aborted',
  PENDING: 'pending',
};

/**
 * Collapse a raw status value onto one of the display tones. Anything we do
 * not recognise — including `null`/`undefined`/`''` — becomes `'unknown'`,
 * which renders as a dash rather than a dot.
 */
export function scanStatusTone(status?: string | null): ScanStatusTone {
  if (!status) return 'unknown';
  return TONE_BY_STATUS[String(status).toUpperCase()] ?? 'unknown';
}

/**
 * The scan sub statuses, in pipeline order. `key` is the model attribute and
 * `labelKey` the i18n key used for the (rotated) column header.
 */
export const SCAN_SUB_STATUSES = [
  { key: 'clone_job_status', labelKey: 'scans.list.columns.cloning' },
  {
    key: 'product_discovery_status',
    labelKey: 'scans.list.columns.product-discovery',
  },
  { key: 'context_injection_status', labelKey: 'scans.list.columns.context' },
  { key: 'analysis_job_status', labelKey: 'scans.list.columns.analysis' },
  {
    key: 'vdr_delivery_and_publish_status',
    labelKey: 'scans.list.columns.publish',
  },
] as const;
